import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { type ChainflipNetwork } from '@chainflip/utils/chainflip';
import { NATIVE_TOKEN_ADDRESS } from '../assets/tokens/constants';
import { getChainflipNetwork } from '../utils/env';
import { noop } from '../utils/helpers';

type TronContract = Record<
  string,
  (...args: unknown[]) => { call: () => Promise<{ toString: () => string }> }
>;

type TronRawTransaction = Record<string, unknown>;

export interface TronWeb {
  defaultAddress: { base58: string | false; hex: string | false };
  trx: {
    getBalance: (address: string) => Promise<number>;
    sign: (transaction: TronRawTransaction) => Promise<TronRawTransaction>;
    sendRawTransaction: (
      signedTransaction: TronRawTransaction,
    ) => Promise<{ result: boolean; txid: string }>;
  };
  contract: (abi?: object[], address?: string) => TronContract;
  transactionBuilder: {
    sendTrx: (to: string, amount: number, from: string) => Promise<TronRawTransaction>;
    triggerSmartContract: (
      contractAddress: string,
      functionSelector: string,
      options: Record<string, unknown>,
      parameters: { type: string; value: string }[],
      issuerAddress: string,
    ) => Promise<{ transaction: TronRawTransaction; result: { result: boolean } }>;
  };
}

interface TronEIP6963Provider {
  request: (params: { method: string; params?: unknown[] }) => Promise<unknown>;
  on: (event: string, handler: (...args: unknown[]) => void) => void;
  off?: (event: string, handler: (...args: unknown[]) => void) => void;
}

declare global {
  interface Window {
    tronWeb?: TronWeb;
  }
}

export interface TronWalletState {
  address: string | null;
  isConnected: boolean;
  connect: () => Promise<void>;
  disconnect: () => void;
}

const TronWalletContext = createContext<TronWalletState | null>(null);

const DEFAULT_STATE: TronWalletState = {
  address: null,
  isConnected: false,
  connect: () => Promise.resolve(),
  disconnect: () => undefined,
};

const envToTronNetworkMap: Record<ChainflipNetwork | 'unknown', string | null> = {
  mainnet: '0x2b6653dc', // mainnet
  perseverance: '0xcd8690dc', // nile testnet
  backspin: null,
  sisyphos: null,
  unknown: null,
};

export const TronProvider = ({ children }: { children: ReactNode }) => {
  const [address, setAddress] = useState<string | null>(null);
  const providerRef = useRef<TronEIP6963Provider | null>(null);
  const network = envToTronNetworkMap[getChainflipNetwork()];

  const switchNetwork = useCallback(async () => {
    if (network && window.tronWeb?.defaultAddress) {
      await providerRef.current
        ?.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: network }],
        })
        .catch(noop);
    }
  }, [network]);

  useEffect(() => {
    const handleAnnounce = (event: Event) => {
      const { info, provider } = (
        event as CustomEvent<{ info: { name: string }; provider: TronEIP6963Provider }>
      ).detail;

      if (info?.name !== 'TronLink') return;

      providerRef.current = provider;

      // Snapshot initial state if already connected
      const addr = window.tronWeb?.defaultAddress?.base58;
      if (addr) setAddress(addr);

      provider.on('accountsChanged', (accounts) => {
        const [first] = accounts as string[];
        setAddress(first || null);
      });

      provider.on('chainChanged', async () => {
        switchNetwork();
      });

      provider.on('connect', async () => {
        const currentAddr = window.tronWeb?.defaultAddress?.base58;
        if (currentAddr) setAddress(currentAddr);
        switchNetwork();
      });
    };

    window.addEventListener('TIP6963:announceProvider', handleAnnounce);
    // Ask any installed wallets to announce themselves
    window.dispatchEvent(new Event('TIP6963:requestProvider'));

    return () => {
      window.removeEventListener('TIP6963:announceProvider', handleAnnounce);
    };
  }, [switchNetwork]);

  const connect = useCallback(async () => {
    const provider = providerRef.current;
    if (!provider) {
      window.open('https://www.tronlink.org/', '_blank');
      return;
    }
    const accounts = (await provider
      .request({ method: 'eth_requestAccounts' })
      .catch(noop)) as string[];
    if (accounts.length) setAddress(accounts[0] as string);
  }, []);

  const disconnect = useCallback(() => {
    setAddress(null);
  }, []);

  const value = useMemo(
    () => ({ address, isConnected: address !== null, connect, disconnect }),
    [address, connect, disconnect],
  );

  return <TronWalletContext.Provider value={value}>{children}</TronWalletContext.Provider>;
};

export const useTronWallet = (): TronWalletState => {
  const ctx = useContext(TronWalletContext);
  return ctx ?? DEFAULT_STATE;
};

export async function executeTronTransfer({
  tronWeb,
  tokenAddress,
  depositAddress,
  amount,
  fromAddress,
}: {
  tronWeb: TronWeb;
  tokenAddress: string;
  depositAddress: string;
  amount: bigint;
  fromAddress: string;
}): Promise<string> {
  if (tokenAddress === NATIVE_TOKEN_ADDRESS) {
    const tx = await tronWeb.transactionBuilder.sendTrx(
      depositAddress,
      Number(amount),
      fromAddress,
    );
    const signedTx = await tronWeb.trx.sign(tx);
    const result = await tronWeb.trx.sendRawTransaction(signedTx);
    if (!result.result) throw new Error('TRX transfer was not accepted by the network');
    return result.txid;
  }

  const triggerResult = await tronWeb.transactionBuilder.triggerSmartContract(
    tokenAddress,
    'transfer(address,uint256)',
    {},
    [
      { type: 'address', value: depositAddress },
      { type: 'uint256', value: amount.toString() },
    ],
    fromAddress,
  );
  const signedTx = await tronWeb.trx.sign(triggerResult.transaction);
  const result = await tronWeb.trx.sendRawTransaction(signedTx);
  if (!result.result) throw new Error('TRC20 transfer was not accepted by the network');
  return result.txid;
}
